/**
 * Guinea phone number normalisation (strict — for partners, drivers, MoMo).
 *
 * Rules (matches backend `_normalise_phone_digits` + `_wa_phone` semantics):
 * - Strip all non-digit characters.
 * - Strip leading "00" (international escape) or leading "0" (national prefix).
 * - If we end up with 8 or 9 digits, prepend Guinea country code "224".
 * - Otherwise return the digit string as-is (already international).
 *
 * Also returns a validity flag: a normalised Guinea number should be 12 digits
 * (224 + 9-digit local), acceptable range is 11–15 digits (E.164 general).
 */
export function normalisePhoneGuinea(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/[^\d]/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = d.replace(/^0+/, "");
  if (d.length === 8 || d.length === 9) d = "224" + d;
  return d;
}

/**
 * International-friendly normalisation (for diaspora clients, MFA, account phone).
 *
 * - Strips all non-digit characters.
 * - Strips leading "00" (international escape).
 * - If we get 8-9 digits, ASSUMES Guinea (adds 224) — same as strict.
 * - Otherwise keeps the digits as-is (any country code accepted).
 * - Never mutilates a valid foreign number (e.g. French 33612345678 stays intact).
 *
 * A number is valid if the digit-only representation has between 8 and 15 chars.
 */
export function normalisePhoneInternational(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/[^\d]/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  // Only add 224 prefix if length matches a local Guinea number
  if (d.length === 8 || (d.length === 9 && !d.startsWith("0"))) d = "224" + d;
  // Local number starting with 0 → strip the 0 + assume Guinea only if 10 digits
  else if (d.startsWith("0") && d.length === 10) d = "224" + d.slice(1);
  return d;
}

/** Human-readable display, e.g. "+224 622 33 44 55" — used in UI after normalise. */
export function formatPhoneGuinea(raw) {
  const d = normalisePhoneGuinea(raw);
  if (!d) return "";
  if (d.length !== 12 || !d.startsWith("224")) return "+" + d;
  const local = d.slice(3);
  return `+224 ${local.slice(0, 3)} ${local.slice(3, 5)} ${local.slice(5, 7)} ${local.slice(7)}`;
}

/** Returns true if the raw input normalises to a plausible Guinea number. */
export function isValidPhoneGuinea(raw) {
  const d = normalisePhoneGuinea(raw);
  return d.length >= 11 && d.length <= 15;
}

/** Returns true if the raw input normalises to any plausible international number. */
export function isValidPhoneInternational(raw) {
  const d = normalisePhoneInternational(raw);
  return d.length >= 8 && d.length <= 15;
}
